'use client';
import classNames from 'classnames';

import { useConfig } from '@/contexts/ConfigContext';
import { Organizations } from '@/types/organization';
import Future2Logo from '@/svg/logo-future2';
import NovatioLogo from '@/svg/logo-novatio';
import PrequelLogo from '@/svg/logo';

const logoShadowClass = 'drop-shadow-[0_4px_10px_rgba(0,0,0,0.18)]';

type LogoVariant = 'header' | 'icon' | 'display';

const logoVariantClass: Record<LogoVariant, string> = {
  header:
    'flex h-9 max-h-9 max-w-[150px] items-center justify-center [&>img]:h-full [&>img]:max-w-full [&>img]:object-contain [&>img]:w-auto [&>svg]:h-full [&>svg]:w-auto',
  icon: 'flex h-full w-full items-center justify-center [&>img]:h-full [&>img]:w-full [&>img]:object-contain [&>svg]:h-full [&>svg]:w-full',
  display:
    'flex w-full max-w-[400px] items-center justify-center [&>img]:h-auto [&>img]:max-h-[220px] [&>img]:w-full [&>img]:object-contain [&>svg]:h-auto [&>svg]:max-h-[220px] [&>svg]:w-full'
};

const Logo = ({ variant = 'header', className }: { variant?: LogoVariant; className?: string }) => {
  const config = useConfig();
  const orgId = config?.org_id;
  const schoolLogo = config?.school_logo?.trim();
  const isFuture2 = orgId === Organizations.FUTURE2_MARTINEZ || orgId === Organizations.FUTURE2_GREGG;
  const wrapperClassName = classNames(logoVariantClass[variant], logoShadowClass, className, {
    'text-blackMint': orgId === Organizations.NOVATIO && !schoolLogo,
    'text-white': isFuture2 && !schoolLogo && variant !== 'display',
    'text-black': isFuture2 && !schoolLogo && variant === 'display'
  });

  if (schoolLogo) {
    return (
      <span className={wrapperClassName} data-testid={`school-logo-${variant}`}>
        <img src={schoolLogo} alt={config?.organization?.name ?? 'School logo'} />
      </span>
    );
  }

  return (
    <span className={wrapperClassName} data-testid={`school-logo-${variant}`}>
      {orgId === Organizations.GT_SCHOOL ? (
        <img src="/assets/gt-logo.png" alt="GT School" />
      ) : orgId === Organizations.NOVATIO ? (
        <NovatioLogo />
      ) : orgId === Organizations.UNBOUND ? (
        <img src="/assets/unbound-logo.svg" alt="Unbound School" />
      ) : isFuture2 ? (
        <Future2Logo />
      ) : (
        <PrequelLogo />
      )}
    </span>
  );
};

export default Logo;
